import { ChangeEvent, FC } from 'react';
import moment from 'moment';
import { datetimeFormat } from 'utils/datetime';
import { ExceptionDate } from 'interfaces/exceptionDate';
import { PractitionerException } from 'interfaces/practitionerException';
import { useLocation } from 'react-router-dom';
import { ROUTES } from 'utils/constants/routes';
import { Operatory } from 'interfaces/operatory';
import { sortBy } from 'lodash';

export interface OptionClinicException extends ExceptionDate {
  checked: boolean;
}

export interface OptionPractitionerException
  extends Required<PractitionerException> {
  checked: boolean;
}

interface OverrideModalBodyProps {
  options: OptionClinicException[] | OptionPractitionerException[];
  selectAllRef: React.MutableRefObject<HTMLInputElement | null>;
  handleChangeCheckbox: (id: string, isChecked: boolean) => void;
  handleChangeSelectAll: (e: ChangeEvent<HTMLInputElement>) => void;
  totalSelectOptions: number;
  displayHour: (
    option: OptionClinicException | OptionPractitionerException
  ) => string;
  isPractitionerAdvancedSchedule?: boolean;
  operatories?: Operatory[];
}

const OverrideModalBody: FC<OverrideModalBodyProps> = ({
  options,
  selectAllRef,
  handleChangeCheckbox,
  handleChangeSelectAll,
  totalSelectOptions,
  displayHour,
  isPractitionerAdvancedSchedule,
  operatories,
}) => {
  const location = useLocation();

  const isInPractitionerPage = location.pathname.includes(ROUTES.PRACTITIONER);

  const isOperatoriesFieldDisplayed =
    isPractitionerAdvancedSchedule && isInPractitionerPage;

  const getGridColumnClassname = () => {
    if (isOperatoriesFieldDisplayed) {
      return 'grid-cols-[70px_1fr_1fr_1fr_1fr]';
    }
    if (isInPractitionerPage) {
      return 'grid-cols-[70px_1fr_1fr_1fr]';
    }
    return 'grid-cols-[70px_1fr_1fr]';
  };

  const isYearInTheFuture = (dateString: string) => {
    return moment().isBefore(moment(dateString, 'YYYY-MM-DD'), 'years');
  };

  const renderOperatories = (option: OptionPractitionerException) => {
    if (!option.isClosed && option.operatories?.length !== 0) {
      return sortBy(option.operatories.map((item) => item.name)).join(', ');
    }

    if (option.isClosed && option.operatories.length === 0) {
      return 'None';
    }

    return sortBy(operatories?.map((item) => item.name)).join(', ');
  };

  const renderLunchBreaks = (option: OptionPractitionerException) => {
    if (option.lunchBreaks.length === 0 || option.isClosed) {
      return 'None';
    }
    return option.lunchBreaks
      .map((breakSlot) => {
        const startTime = datetimeFormat({
          dateString: breakSlot.start!,
          format: 'HH:mm:ss',
          pattern: 'hh:mmA',
        });
        const endTime = datetimeFormat({
          dateString: breakSlot.end!,
          format: 'HH:mm:ss',
          pattern: 'hh:mmA',
        });
        return `${startTime} - ${endTime}`;
      })
      .join(', ');
  };

  return (
    <>
      <div
        className={`border-b-[2px] border-grey pb-0.9 mt-2.4 grid ${getGridColumnClassname()} items-center`}
      >
        <div className="flex items-center">
          <input
            className="w-2.2 h-2.2"
            type="checkbox"
            id="selectAll"
            ref={selectAllRef}
            onChange={handleChangeSelectAll}
          />
          <span className="text-[1rem] leading-[1.8rem] ml-[6px]">
            ({totalSelectOptions})
          </span>
        </div>
        <span className="font-bold">Date</span>
        <span className="font-bold">Hours</span>
        {isOperatoriesFieldDisplayed && (
          <span className="font-bold">Operatories</span>
        )}
        {isInPractitionerPage && (
          <span className="font-bold">Lunch Break(s)</span>
        )}
      </div>
      <div className="max-h-[28rem] scrollbar overflow-auto text-darkest-grey ">
        {options.map((option, index) => (
          <div
            key={option.id}
            className={`grid min-h-[4rem] py-0.6 items-center border-b border-b-light-grey ${
              index % 2 ? 'bg-lightest-grey' : 'bg-white'
            } ${getGridColumnClassname()}`}
          >
            <input
              className="w-2.2 h-2.2"
              type="checkbox"
              id={option.id}
              checked={option.checked}
              onChange={(e) =>
                handleChangeCheckbox(option.id, e.target.checked)
              }
            />
            <label htmlFor={option.id}>
              {datetimeFormat({
                dateString: option.date,
                format: 'YYYY-MM-DD',
                pattern: `ddd, MMM DD${
                  isYearInTheFuture(option.date) ? ', YYYY' : ''
                }`,
              })}
            </label>
            <span>{displayHour(option)}</span>
            {isOperatoriesFieldDisplayed && (
              <span>
                {renderOperatories(option as OptionPractitionerException)}
              </span>
            )}
            {isInPractitionerPage && (
              <span>
                {renderLunchBreaks(option as OptionPractitionerException)}
              </span>
            )}
          </div>
        ))}
      </div>
    </>
  );
};

export default OverrideModalBody;
